import type { ItemInsights, ItemReport } from '@erp/shared';
import { BarChart3, Coins, Eye, Globe, Package, ShoppingCart, Store, TrendingUp, Trophy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Card, Skeleton } from '../../../components/shadcn/index.js';
import { EmptyState, StatCard } from '../../../components/ui/index.js';
import { api } from '../../../lib/api.js';
import { formatDate, formatNumber } from '../../../lib/intl.js';
import { formatMoney } from '../../../lib/money.js';

import { fmtQty } from './profileFormat.js';
import { LoadError, moneyList, SectionTitle } from './ProfileParts.js';

/**
 * How the product sells: units, revenue, the register/online split and the
 * monthly trend (the shop's "sold" basis, shared with the product list), then
 * the customers who buy the most of it (posted invoices, base units).
 */
export function ItemReportTab({ itemId, insights, baseUnit, canParties }: {
  itemId: string;
  insights: ItemInsights | null;
  baseUnit: string;
  canParties: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation('items');
  const locale = i18n.language;
  const [rep, setRep] = useState<ItemReport | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!itemId) return undefined;
    let active = true;
    setRep(null); setError(false);
    void api().get<ItemReport>(`/items/${itemId}/report`)
      .then((r) => { if (active) setRep(r.data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [itemId]);

  if (error) return <LoadError />;
  if (!rep) return <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>;

  const nf = (v: number): string => formatNumber(v, { locale });
  const money = (m: { currencyCode: string; amount: string }): string => formatMoney(m.amount, { locale, currency: m.currencyCode });
  const revLabel = (rev: ItemReport['revenue']): string => (rev.length === 0 ? '—' : rev.map(money).join(' · '));
  const dateStr = (iso: string | null): string => (iso ? formatDate(iso, { locale }) : '—');
  const totalUnits = Number(rep.totalUnits);
  const top = insights?.topCustomers ?? [];
  const maxMonth = rep.monthly.reduce((m, x) => Math.max(m, Number(x.units)), 0) || 1;
  const nothingYet = totalUnits === 0 && rep.views === 0 && rep.salesCount === 0;

  return (
    <div className="flex flex-col gap-4">
      {nothingYet ? (
        <Card className="p-6">
          <EmptyState
            icon={<BarChart3 className="h-7 w-7" aria-hidden />}
            title={t('detail.report.empty', { defaultValue: 'No sales data yet' })}
            description={t('detail.report.emptyHint', { defaultValue: 'Units sold, revenue and trends will appear here as this item sells.' })}
          />
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard label={t('detail.report.unitsSold', { defaultValue: 'Units sold' })} value={nf(totalUnits)} icon={ShoppingCart} tone="success" />
            <StatCard label={t('detail.report.revenue', { defaultValue: 'Revenue' })} value={revLabel(rep.revenue)} icon={Coins} tone="info" />
            <StatCard label={t('detail.report.sales', { defaultValue: 'Sales' })} value={nf(rep.salesCount)} icon={Package} />
            <StatCard label={t('detail.report.views', { defaultValue: 'Store views' })} value={nf(rep.views)} icon={Eye} />
          </div>

          <Card className="p-6">
            <SectionTitle>{t('detail.report.byChannel', { defaultValue: 'By channel' })}</SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {([
                { key: 'pos', icon: Store, label: t('detail.report.pos', { defaultValue: 'Point of sale' }), ch: rep.pos },
                { key: 'online', icon: Globe, label: t('detail.report.online', { defaultValue: 'Online store' }), ch: rep.online },
              ] as const).map(({ key, icon: Icon, label, ch }) => (
                <div key={key} className="rounded-xl border border-border bg-bg-subtle p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold"><Icon className="h-4 w-4 text-fg-muted" aria-hidden />{label}</div>
                  <div className="mt-3 flex items-baseline justify-between">
                    <span className="text-2xl font-bold tabular-nums">{nf(Number(ch.units))}</span>
                    <span className="text-xs text-fg-muted">{t('detail.report.salesCount', { defaultValue: '{{n}} sales', n: ch.salesCount })}</span>
                  </div>
                  <div className="mt-1 font-mono text-sm text-brand">{revLabel(ch.revenue)}</div>
                </div>
              ))}
            </div>
          </Card>

          {rep.monthly.length > 0 && (
            <Card className="p-6">
              <SectionTitle icon={TrendingUp}>{t('detail.report.monthly', { defaultValue: 'Units sold per month' })}</SectionTitle>
              <div className="flex flex-col gap-2">
                {rep.monthly.map((m) => (
                  <div key={m.month} className="flex items-center gap-3">
                    <span className="w-16 shrink-0 font-mono text-xs text-fg-muted">{m.month}</span>
                    <div className="h-5 flex-1 overflow-hidden rounded-md bg-bg-subtle">
                      <div className="h-full rounded-md bg-brand transition-[width] duration-500 ease-out" style={{ width: `${Math.max(4, (Number(m.units) / maxMonth) * 100)}%` }} />
                    </div>
                    <span className="w-14 shrink-0 text-end font-mono text-xs font-semibold tabular-nums">{nf(Number(m.units))}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <div className="flex flex-wrap gap-x-6 gap-y-1 px-1 text-xs text-fg-muted">
            <span>{t('detail.report.firstSold', { defaultValue: 'First sold' })}: <span className="text-fg">{dateStr(rep.firstSoldAt)}</span></span>
            <span>{t('detail.report.lastSold', { defaultValue: 'Last sold' })}: <span className="text-fg">{dateStr(rep.lastSoldAt)}</span></span>
          </div>
        </>
      )}

      {top.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="px-4 pt-4">
            <SectionTitle icon={Trophy}>{t('profile.customers.title', { defaultValue: 'Best customers for this product' })}</SectionTitle>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-border bg-bg-subtle text-xs font-semibold uppercase tracking-wide text-fg-muted">
                  <th className="w-10 px-3 py-2.5 text-start">#</th>
                  <th className="px-3 py-2.5 text-start">{t('profile.docs.customer', { defaultValue: 'Customer' })}</th>
                  <th className="px-3 py-2.5 text-end">{t('profile.customers.units', { defaultValue: 'Bought ({{unit}})', unit: baseUnit })}</th>
                  <th className="px-3 py-2.5 text-end">{t('profile.customers.invoices', { defaultValue: 'Invoices' })}</th>
                  <th className="px-3 py-2.5 text-end">{t('detail.report.revenue', { defaultValue: 'Revenue' })}</th>
                  <th className="px-3 py-2.5 text-start">{t('profile.customers.last', { defaultValue: 'Last bought' })}</th>
                </tr>
              </thead>
              <tbody>
                {top.map((c, i) => (
                  <tr key={c.partyId ?? `none-${i}`} className="border-b border-border last:border-0">
                    <td className="px-3 py-2.5 font-mono text-xs text-fg-subtle">{i + 1}</td>
                    <td className="px-3 py-2.5 font-medium">
                      {c.partyId && canParties
                        ? <Link to={`/sales/parties/${c.partyId}`} className="text-brand hover:underline">{c.partyName ?? '—'}</Link>
                        : (c.partyName ?? '—')}
                    </td>
                    <td className="px-3 py-2.5 text-end font-mono font-semibold tabular-nums">{fmtQty(c.units, locale)}</td>
                    <td className="px-3 py-2.5 text-end font-mono tabular-nums text-fg-muted">{nf(c.invoices)}</td>
                    <td className="px-3 py-2.5 text-end font-mono tabular-nums">{moneyList(c.revenue, locale)}</td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-fg-muted">{formatDate(c.lastDate, { locale })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
